import { NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, OnDestroy, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { filter, map } from 'rxjs/operators';
import { SidenavService } from '../../sidenav/sidenav.service';
import { SidenavItem } from '../../sidenav/sidenav-item/sidenav-item.interface';
import { AppThemeService } from 'src/app/main/shared/services/app-theme.service';
import { FeatureFlagService } from 'src/app/main/shared/services/feature-flag.service';
import { selectUserSession } from 'src/app/main/shared/services/store/user/selector';
import { selectSystemInfo } from 'src/app/main/shared/services/store/misc/selectors';
import { compareVersions } from 'src/app/main/shared/utils/version-utils';
import { IFeatureFlags } from 'src/app/core/dto';

/** How long the pointer rests on the rail before it peeks open, so crossing it doesn't flash it. */
const PEEK_DELAY_MS = 150;

/**
 * The Steel side nav: pinned open at 240 px (pushes content), or a 66 px icon rail that peeks open
 * over the page on hover or keyboard focus. Groups expand inline, one at a time, reusing the classic
 * nav's items, ordering, claims and feature flags from SidenavService.
 */
@Component({
  selector: 'app-steel-nav',
  templateUrl: './steel-nav.component.html',
  styleUrls: ['./steel-nav.component.scss'],
  imports: [NgTemplateOutlet, RouterModule, TranslateModule],
})
export class SteelNavComponent implements OnDestroy {
  private sidenavService = inject(SidenavService);
  private appTheme = inject(AppThemeService);
  private router = inject(Router);
  private store = inject(Store);
  private featureFlags = inject(FeatureFlagService);
  private host: ElementRef<HTMLElement> = inject(ElementRef);

  private peekTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly _peek = signal(false);

  readonly pinned = this.appTheme.navPinned;
  readonly peek = computed(() => !this.pinned() && this._peek());
  readonly open = computed(() => this.pinned() || this.peek());

  private readonly allItems = toSignal(this.sidenavService.items$, { initialValue: [] });
  private readonly claims = toSignal(
    this.store.select(selectUserSession).pipe(map((session) => session?.claims ?? null)),
    { initialValue: null }
  );
  private readonly features = toSignal(this.featureFlags.features$, {
    initialValue: {} as IFeatureFlags,
  });
  private readonly currentlyOpen = toSignal(this.sidenavService.currentlyOpen$, {
    initialValue: [],
  });
  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  private readonly systemInfo = toSignal(this.store.select(selectSystemInfo));
  readonly serverVersion = computed(() => this.systemInfo()?.version ?? '');
  readonly latestVersion = computed(() => this.systemInfo()?.latestReleaseVersion?.version ?? '');
  /** Positive when a newer release is out; negative when this server is ahead of the release. */
  readonly versionComparison = computed(() => {
    const latest = this.latestVersion();
    const current = this.serverVersion();
    return latest && current ? compareVersions(latest, current) : 0;
  });

  /** The visible tree, sorted, with hidden sub-items removed and groups with none left dropped. */
  readonly items = computed(() => {
    const claims = this.claims();
    const features = this.features();
    const visible = (item: SidenavItem) => this.isShown(item, claims, features);
    return this.sidenavService
      .sortRecursive([...this.allItems()], 'position')
      .filter(visible)
      .map((item) =>
        item.subItems?.length ? { item, subItems: item.subItems.filter(visible) } : { item }
      )
      .filter((entry) => !entry.subItems || entry.subItems.length > 0);
  });

  ngOnDestroy(): void {
    clearTimeout(this.peekTimer);
  }

  togglePinned(): void {
    this.appTheme.toggleNavPinned();
    this._peek.set(false);
  }

  isOpen(item: SidenavItem): boolean {
    return this.currentlyOpen().includes(item);
  }

  toggle(item: SidenavItem): void {
    this.sidenavService.toggleItemOpen(item);
  }

  /** A group holding the current page: its row is marked so the rail still shows where you are. */
  isActiveGroup(subItems: SidenavItem[]): boolean {
    const url = this.url();
    return subItems.some((sub) => !sub.href && this.matches(url, sub.routeOrFunction));
  }

  startPeek(): void {
    if (this.pinned() || this._peek()) {
      return;
    }
    clearTimeout(this.peekTimer);
    this.peekTimer = setTimeout(() => this._peek.set(true), PEEK_DELAY_MS);
  }

  /** Keyboard focus opens the peek at once; there is no pointer to wait for. */
  onFocusIn(): void {
    if (!this.pinned()) {
      clearTimeout(this.peekTimer);
      this._peek.set(true);
    }
  }

  endPeek(): void {
    clearTimeout(this.peekTimer);
    this._peek.set(false);
  }

  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (!next || !this.host.nativeElement.contains(next)) {
      this.endPeek();
    }
  }

  /** Following a link from the peek closes it, so the page you went to isn't left covered. */
  onNavigate(): void {
    if (this.peek()) {
      this.endPeek();
      (document.activeElement as HTMLElement | null)?.blur();
    }
  }

  private matches(url: string, route: unknown): boolean {
    const path = url.split(/[?#]/)[0];
    return typeof route === 'string' && (path === route || path.startsWith(route + '/'));
  }

  private isShown(
    item: SidenavItem,
    claims: string[] | null,
    features: IFeatureFlags
  ): boolean {
    if (item.claims) {
      return !!claims && item.claims.split(' ').some((claim) => claims.includes(claim));
    }
    if (item.featureFlag) {
      return !!(features as Record<string, boolean>)[item.featureFlag];
    }
    return true;
  }
}
